import React, { useState } from 'react'
import { Send, Bot, User } from 'lucide-react'
import api from '../services/api'

const AIChat = () => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I am the Verifi AI assistant. I can help you with product authentication queries, counterfeit detection insights, and system analytics. How can I help you today?' }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)

  const sendMessage = async () => {
    if (!input.trim()) return
    const userMessage = { role: 'user', content: input.trim() }
    setMessages(prev => [...prev, userMessage])
    setInput('')
    setLoading(true)

    try {
      const response = await api.post('/scanner/verify', { tokenHash: 'AI-QUERY' })
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Based on our database analysis:\n\n- Total products tracked: ${response.data?.data?.totalProducts || 'N/A'}\n- System status: Active\n\nI can help you look up specific products, review authentication reports, or provide analytics insights.`
      }])
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I can help you with product verification, counterfeit detection, and system analytics. Try scanning a product or asking about specific brands or regions.'
      }])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)]">
      <div className="flex items-center space-x-3 mb-4">
        <div className="p-2 bg-green-50 rounded-xl">
          <Bot className="h-6 w-6 text-green-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Assistant</h1>
          <p className="text-sm text-gray-500">Intelligent authentication insights</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex items-start space-x-2 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
              <div className={`p-1.5 rounded-full flex-shrink-0 ${
                msg.role === 'user' ? 'bg-green-50' : 'bg-gray-100'
              }`}>
                {msg.role === 'user' ? (
                  <User className="h-4 w-4 text-green-600" />
                ) : (
                  <Bot className="h-4 w-4 text-gray-600" />
                )}
              </div>
              <div className={`px-4 py-2 rounded-2xl ${
                msg.role === 'user' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-900'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-2 rounded-2xl">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex space-x-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Ask about product authentication..."
          className="flex-1 px-4 py-3 border border-gray-200 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-3 rounded-xl transition-colors"
        >
          <Send className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}

export default AIChat
